/**
 * PatientVoice — conversación bidireccional con el paciente simulado.
 *
 * Stack:
 *   - STT: Web Speech API (SpeechRecognition) — nativo del navegador, $0
 *   - LLM: Kimi K2.5 via /ai/patient-voice — responde como paciente
 *   - TTS: Web Speech API (SpeechSynthesis) — nativo, $0, voz en español
 *
 * La voz se ajusta por edad/sexo del paciente + emoción detectada por IA:
 *   - Pitch alto para niños, bajo para ancianos
 *   - Rate lento si está disneico/cansado, normal si estable
 *
 * El alumno mantiene presionado un botón para hablar (push-to-talk)
 * y al soltar se envía la pregunta. El paciente responde en 1-3 seg.
 */

import { authService } from '../auth/AuthService';
import type { Patient } from '../medical/Patient';

export interface PatientVoiceTurn {
  role: 'student' | 'patient';
  text: string;
  at: number;
  emotion?: string;
}

type EmotionType = 'neutral' | 'pained' | 'disneic' | 'confused' | 'scared' | 'tired';

interface PatientVoiceResponse {
  response: string;
  emotion: EmotionType;
  voice_pitch: number;
  voice_rate: number;
}

// Tipos mínimos para SpeechRecognition (TS no los trae por default).
// Solo declaramos las propiedades que usamos.
interface MinimalRecognitionResultItem { transcript: string }
interface MinimalRecognitionResult { 0: MinimalRecognitionResultItem; readonly length: number }
interface MinimalRecognitionResultList { 0: MinimalRecognitionResult; readonly length: number }
interface MinimalRecognitionEvent { results: MinimalRecognitionResultList }
interface MinimalRecognitionErrorEvent { error: string }
interface MinimalRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: MinimalRecognitionEvent) => void) | null;
  onerror: ((e: MinimalRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
interface SpeechRecognitionConstructor {
  new (): MinimalRecognition;
}
const SpeechRecognitionImpl: SpeechRecognitionConstructor | undefined =
  (window as unknown as { SpeechRecognition?: SpeechRecognitionConstructor }).SpeechRecognition
  || (window as unknown as { webkitSpeechRecognition?: SpeechRecognitionConstructor }).webkitSpeechRecognition;

export class PatientVoice {
  private recognition: MinimalRecognition | null = null;
  private currentPatient: Patient | null = null;
  private history: PatientVoiceTurn[] = [];
  private isListening = false;
  private onTurn: ((turn: PatientVoiceTurn) => void) | null = null;
  private onStateChange: ((state: 'idle' | 'listening' | 'thinking' | 'speaking') => void) | null = null;
  private preferredVoice: SpeechSynthesisVoice | null = null;

  setPatient(patient: Patient | null): void {
    this.currentPatient = patient;
    this.history = [];
    this.pickVoice();
  }

  onTurnAdded(cb: (turn: PatientVoiceTurn) => void): void {
    this.onTurn = cb;
  }

  onState(cb: (s: 'idle' | 'listening' | 'thinking' | 'speaking') => void): void {
    this.onStateChange = cb;
  }

  isSupported(): boolean {
    return !!SpeechRecognitionImpl && !!window.speechSynthesis;
  }

  /** Arranca la escucha (push-to-talk). Llamar al apretar el botón. */
  startListening(): void {
    if (!SpeechRecognitionImpl || this.isListening) return;
    window.speechSynthesis?.cancel(); // detener voz del paciente si estaba hablando
    this.recognition = new SpeechRecognitionImpl();
    this.recognition.lang = 'es-CL';
    this.recognition.continuous = false;
    this.recognition.interimResults = false;
    this.recognition.maxAlternatives = 1;

    this.recognition.onresult = (e) => {
      const text = e.results[0]?.[0]?.transcript?.trim();
      if (text) this.handleStudentQuestion(text);
    };
    this.recognition.onerror = (e) => {
      console.warn('[PatientVoice] STT error:', e.error);
      this.isListening = false;
      this.onStateChange?.('idle');
    };
    this.recognition.onend = () => {
      this.isListening = false;
    };

    try {
      this.recognition.start();
      this.isListening = true;
      this.onStateChange?.('listening');
    } catch (err) {
      console.warn('[PatientVoice] start failed:', err);
      this.onStateChange?.('idle');
    }
  }

  /** Detiene la escucha manualmente (soltar push-to-talk). */
  stopListening(): void {
    if (!this.recognition || !this.isListening) return;
    try { this.recognition.stop(); } catch { /* ignore */ }
    this.isListening = false;
  }

  /** Alumno escribe la pregunta en lugar de hablar (fallback sin micrófono). */
  ask(questionText: string): void {
    if (!questionText.trim()) return;
    this.handleStudentQuestion(questionText.trim());
  }

  getHistory(): PatientVoiceTurn[] {
    return [...this.history];
  }

  // ─────────────────────────────────────────────────────────
  // Lógica principal
  // ─────────────────────────────────────────────────────────

  private async handleStudentQuestion(text: string): Promise<void> {
    if (!this.currentPatient) return;
    const studentTurn: PatientVoiceTurn = { role: 'student', text, at: Date.now() };
    this.history.push(studentTurn);
    this.onTurn?.(studentTurn);

    this.onStateChange?.('thinking');
    try {
      const p = this.currentPatient.data;
      const dxNode = p.nodes.find(n => n.type === 'diagnosis');
      // Vitales actuales desde los nodos del paciente
      const vitals: Record<string, number | string | undefined> = {};
      const codeMap: Record<string, string> = {
        '8867-4': 'hr', '8480-6': 'sbp', '8462-4': 'dbp',
        '59408-5': 'spo2', '2708-6': 'spo2', '9279-1': 'rr', '8310-5': 'temp',
      };
      p.nodes.forEach(n => {
        const k = codeMap[n.concept.code];
        if (k && n.valueQuantity?.value !== undefined) vitals[k] = n.valueQuantity.value;
      });

      const res = await authService.request<PatientVoiceResponse>('/ai/patient-voice', {
        method: 'POST',
        body: JSON.stringify({
          case_id: p.id,
          chief_complaint: p.chiefComplaint ?? '',
          diagnosis: dxNode?.concept.display,
          age: p.demographics.age,
          sex: p.demographics.sex,
          vitals,
          question: text,
          history: this.history.slice(-10).map(t => ({ role: t.role, text: t.text })),
        }),
      });

      const patientTurn: PatientVoiceTurn = {
        role: 'patient',
        text: res.response,
        at: Date.now(),
        emotion: res.emotion,
      };
      this.history.push(patientTurn);
      this.onTurn?.(patientTurn);

      this.speak(res.response, res.voice_pitch, res.voice_rate);
    } catch (e) {
      const errTurn: PatientVoiceTurn = {
        role: 'patient',
        text: '(no puedo responder ahora)',
        at: Date.now(),
      };
      this.history.push(errTurn);
      this.onTurn?.(errTurn);
      this.onStateChange?.('idle');
      console.warn('[PatientVoice] LLM error:', e);
    }
  }

  /** Sintetiza la respuesta del paciente con Web Speech API. */
  private speak(text: string, pitch: number, rate: number): void {
    if (!window.speechSynthesis) {
      this.onStateChange?.('idle');
      return;
    }
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'es-CL';
    utter.pitch = Math.max(0.5, Math.min(2, pitch));
    utter.rate = Math.max(0.5, Math.min(2, rate));
    utter.volume = 1;
    if (this.preferredVoice) utter.voice = this.preferredVoice;
    utter.onstart = () => this.onStateChange?.('speaking');
    utter.onend = () => this.onStateChange?.('idle');
    utter.onerror = () => this.onStateChange?.('idle');
    window.speechSynthesis.speak(utter);
  }

  /**
   * Selecciona la voz de Web Speech API más apropiada para el paciente actual.
   * Prioridad: español de Chile/Argentina/México, masculina/femenina según sexo.
   */
  private pickVoice(): void {
    if (!window.speechSynthesis) return;
    const apply = (): void => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) return;
      const p = this.currentPatient?.data;
      const preferMale = p?.demographics.sex === 'male';
      const esVoices = voices.filter(v => v.lang.startsWith('es'));
      const candidate = esVoices.find(v => preferMale
        ? /male|hombre|carlos|juan|diego|pablo|jorge/i.test(v.name)
        : /female|mujer|maria|paula|ana|sofia|juana/i.test(v.name))
        ?? esVoices.find(v => v.lang === 'es-CL' || v.lang === 'es-MX' || v.lang === 'es-AR')
        ?? esVoices[0]
        ?? voices[0];
      this.preferredVoice = candidate ?? null;
    };
    // getVoices() puede devolver [] la primera vez; escuchar el evento
    apply();
    window.speechSynthesis.addEventListener('voiceschanged', apply, { once: true });
  }
}

export const patientVoice = new PatientVoice();
